import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import SearchInput from 'src/components/SearchInput';
// import {AccessGroup} from 'src/apis/autoGenerate';
// import {accessGroupServiceDelete} from 'src/apis';
import {AccessGroup} from 'src/apis/autoGenerate/types/accessGroup';
import {accessGroupServiceDelete} from 'src/apis/autoGenerate/services';
import {GridColDef} from '@mui/x-data-grid';
import {PageHeader, EntioDataGrid} from 'src/components/ui';
import {formatDate, getVal} from 'src/utils/common';
import message from 'src/components/Message';
import {useAccessGroups, useCanEdit} from 'src/hooks';
import Spin from 'src/components/Spin';

export default function Devices() {
  const navigate = useNavigate();
  const {loading: loadingAccessGroups, accessGroups, getAccessGroups} = useAccessGroups();
  const canEdit = useCanEdit();
  const [loading, setLoading] = useState(false);
  const [selectedAccessGroup, setSelectedAccessGroup] = useState<AccessGroup>();
  const [accessGroupActionIdAnchorEl, setAccessGroupActionIdAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const open = Boolean(accessGroupActionIdAnchorEl);
  const id = open ? 'action-popover' : undefined;

  useEffect(() => {
    getAccessGroups();
  }, [getAccessGroups]);

  const handleClose = () => {
    setAccessGroupActionIdAnchorEl(null);
  };

  const handleSearchGroupByInfo = (group = '') => {
    getAccessGroups(undefined, group);
  };

  const handleAccessGroupActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    accessGroup: AccessGroup
  ) => {
    setSelectedAccessGroup(accessGroup);
    setAccessGroupActionIdAnchorEl(event.currentTarget);
  };

  const handleManageTenants = () => {
    if (selectedAccessGroup && selectedAccessGroup.id) {
      navigate(`/devices/accessGroup/manageTenants/${selectedAccessGroup.id}`);
    }
  };
  const handleEditGroup = () => {
    if (selectedAccessGroup && selectedAccessGroup.id) {
      navigate(`/devices/accessGroup/edit/${selectedAccessGroup.id}`);
    }
  };

  const handleManageOrgUsers = () => {
    if (selectedAccessGroup && selectedAccessGroup.id) {
      navigate(`/devices/accessGroup/manageOrgUsers/${selectedAccessGroup.id}`);
    }
  };

  const handleRemoveGroup = () => {
    if (selectedAccessGroup && selectedAccessGroup?.id) {
      setLoading(true);
      setAccessGroupActionIdAnchorEl(null);
      accessGroupServiceDelete(selectedAccessGroup.id)
        .then(() => {
          message.success('Remove successful');
          getAccessGroups();
        })
        .catch((e: any) => {
          message.error(e.err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {field: 'name', headerName: 'Access Group', width: 280},
      {
        field: 'deviceCount',
        headerName: 'Device',
        width: 180,
        renderCell: val => {
          return <div>{getVal(val.value)}</div>;
        },
      },
      {
        field: 'tenantCount',
        headerName: 'People',
        width: 200,
        renderCell: val => {
          return <div>{getVal(val.value)}</div>;
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        width: 300,
        renderCell: val => {
          return <div>{formatDate(val.value)}</div>;
        },
      },
      ...(canEdit
        ? [
            {
              field: 'id',
              headerName: '',
              width: 150,
              renderCell: (val: any) => {
                return (
                  <IconButton
                    onClick={e => {
                      handleAccessGroupActionClick(e, val.row);
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ],
    [canEdit]
  );

  return (
    <Box>
      <Spin spinning={loading} />
      <PageHeader
        title="Access Groups"
        actions={
          canEdit ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/devices/accessGroup/add')}
            >
              Add Access Group
            </Button>
          ) : undefined
        }
      />
      <Box sx={{mb: 2}}>
        <SearchInput placeholder="Search Groups" onSearch={handleSearchGroupByInfo} />
      </Box>
      <Box mt={2}>
        <EntioDataGrid
          loading={loadingAccessGroups}
          rows={accessGroups}
          columns={columns}
          emptyMessage="No access groups"
        />
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={accessGroupActionIdAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          {canEdit && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={handleEditGroup}>
                  <ListItemText primary="Edit Access Group" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleManageTenants}>
                  <ListItemText primary="Manage Residents" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleManageOrgUsers}>
                  <ListItemText primary="Manage Org Users" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding onClick={handleRemoveGroup}>
                <ListItemButton>
                  <ListItemText primary="Remove" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Popover>
    </Box>
  );
}
